import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { CalendarCheck, CalendarDays, Stethoscope, Ticket } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { PageHeader } from "@/components/layout/AppShell";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button, LinkButton } from "@/components/ui/Button";
import { Input, Select, Textarea } from "@/components/ui/Field";
import { Alert, EmptyState, LoadingPanel } from "@/components/ui/Feedback";
import { formatDate } from "@/components/ui/DataTable";
import { cn } from "@/lib/cn";

const doctorsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        specialty: z.string().nullable().default(null),
        schedule: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

const confirmationSchema = z.looseObject({
  ticket: z.string(),
  queue_number: z.string(),
  doctor: z.string(),
  specialty: z.string(),
  appointment_date: z.string(),
  rm_number: z.string(),
  patient_name: z.string(),
});

type Confirmation = z.infer<typeof confirmationSchema>;

const GUARANTORS = ["Umum / Pribadi", "BPJS", "Asuransi Swasta", "Perusahaan"];

export function ReservationScreen() {
  const [doctorId, setDoctorId] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [guarantor, setGuarantor] = useState(GUARANTORS[0]);
  const [noBpjs, setNoBpjs] = useState("");
  const [complaint, setComplaint] = useState("");
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [error, setError] = useState<string | null>(null);

  const doctors = useQuery({
    queryKey: ["doctors"],
    queryFn: ({ signal }) => api.get("/api/doctors", doctorsSchema, signal),
  });

  const book = useMutation({
    mutationFn: () =>
      api.post("/api/reservasi", confirmationSchema, {
        doctor_id: doctorId,
        appointment_date: appointmentDate,
        guarantor,
        no_bpjs: noBpjs,
        complaint,
      }),
    onSuccess: (data) => setConfirmation(data),
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : "Reservasi gagal diproses."),
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    book.mutate();
  }

  const selectedDoctor = doctors.data?.items.find((item) => String(item.id) === doctorId);
  // The server rejects past dates; the picker should not offer them either.
  const today = new Date().toISOString().slice(0, 10);

  if (confirmation) {
    return (
      <>
        <PageHeader eyebrow="Berhasil" title="Reservasi terkonfirmasi" />
        <Card className="max-w-xl">
          <CardBody className="space-y-5">
            <div className="flex items-center gap-3">
              <span className="bg-routine-tint grid size-11 shrink-0 place-items-center rounded-full">
                <CalendarCheck className="text-routine-text size-5" />
              </span>
              <div className="min-w-0">
                <p className="font-display text-lg font-bold">Jadwal Anda tercatat</p>
                <p className="text-content-muted text-sm">
                  Tunjukkan nomor antrean saat tiba di rumah sakit.
                </p>
              </div>
            </div>

            <div className="bg-surface-sunken rounded-xl px-5 py-4 text-center">
              <p className="eyebrow mb-1">Nomor antrean</p>
              <p className="tabular font-display text-4xl font-extrabold">
                {confirmation.queue_number}
              </p>
            </div>

            <dl className="divide-hairline divide-y text-sm">
              {[
                ["Tiket", confirmation.ticket],
                ["Pasien", `${confirmation.patient_name} · ${confirmation.rm_number}`],
                ["Dokter", confirmation.doctor],
                ["Poliklinik", confirmation.specialty],
                ["Tanggal", formatDate(confirmation.appointment_date)],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4 py-2.5">
                  <dt className="text-content-subtle shrink-0">{label}</dt>
                  <dd className="tabular min-w-0 text-right font-medium">{value}</dd>
                </div>
              ))}
            </dl>

            <Alert tone="info">
              Pengingat otomatis akan muncul di portal pada pagi hari kunjungan.
            </Alert>

            <div className="flex flex-wrap gap-2">
              <LinkButton to="/patient/dashboard" variant="primary">
                Kembali ke beranda
              </LinkButton>
              <Button
                onClick={() => {
                  setConfirmation(null);
                  setDoctorId("");
                  setAppointmentDate("");
                  setComplaint("");
                }}
              >
                Buat reservasi lain
              </Button>
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Rawat jalan"
        title="Buat Reservasi"
        description="Pilih dokter dan tanggal kunjungan. Nomor antrean diberikan setelah konfirmasi."
      />

      {error && (
        <Alert tone="danger" title="Reservasi gagal" className="mb-4">
          {error}
        </Alert>
      )}

      {/* items-start: grid cells stretch to the tallest sibling by default, so
          the capped doctor list left a band of empty card below itself. */}
      <form onSubmit={handleSubmit} className="grid gap-4 lg:grid-cols-5 lg:items-start">
        <Card className="lg:col-span-3">
          <CardHeader eyebrow="Langkah 1" title="Pilih dokter" />
          {doctors.isPending ? (
            <LoadingPanel label="Memuat daftar dokter" />
          ) : doctors.data && doctors.data.items.length > 0 ? (
            <ul className="divide-hairline max-h-96 divide-y overflow-y-auto">
              {doctors.data.items.map((doctor) => {
                const active = String(doctor.id) === doctorId;
                return (
                  <li key={doctor.id}>
                    <label
                      className={cn(
                        "flex cursor-pointer items-start gap-3 px-5 py-3.5 transition-colors",
                        active ? "bg-accent/10" : "hover:bg-surface-sunken",
                      )}
                    >
                      <input
                        type="radio"
                        name="doctor"
                        value={doctor.id}
                        checked={active}
                        onChange={() => setDoctorId(String(doctor.id))}
                        className="accent-accent mt-1 size-4 shrink-0"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold">{doctor.name}</span>
                        <span className="text-content-muted block text-xs">
                          {doctor.specialty ?? "—"}
                        </span>
                        {doctor.schedule && (
                          <span className="text-content-subtle mt-0.5 block text-xs">
                            {doctor.schedule}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState
              icon={<Stethoscope className="size-5" />}
              title="Belum ada dokter aktif"
              description="Hubungi RS Mediora untuk membuat janji temu."
            />
          )}
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader eyebrow="Langkah 2" title="Detail kunjungan" />
          <CardBody className="space-y-4">
            {selectedDoctor ? (
              <div className="bg-surface-sunken rounded-xl px-4 py-3">
                <p className="text-sm font-semibold">{selectedDoctor.name}</p>
                <p className="text-content-muted text-xs">{selectedDoctor.specialty}</p>
              </div>
            ) : (
              <p className="text-content-subtle text-sm">Pilih dokter terlebih dahulu.</p>
            )}

            <Input
              label="Tanggal kunjungan"
              type="date"
              min={today}
              value={appointmentDate}
              onChange={(event) => setAppointmentDate(event.target.value)}
              required
            />

            <Select
              label="Penjamin"
              value={guarantor}
              onChange={(event) => setGuarantor(event.target.value)}
            >
              {GUARANTORS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </Select>

            {guarantor === "BPJS" && (
              <Input
                label="Nomor BPJS"
                value={noBpjs}
                onChange={(event) => setNoBpjs(event.target.value)}
                hint="Kosongkan bila sudah tersimpan pada profil Anda."
              />
            )}

            <Textarea
              label="Keluhan"
              placeholder="Ceritakan singkat keluhan Anda."
              value={complaint}
              onChange={(event) => setComplaint(event.target.value)}
            />

            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={book.isPending}
              disabled={!doctorId || !appointmentDate}
              icon={<Ticket className="size-4" />}
            >
              Konfirmasi reservasi
            </Button>

            <p className="text-content-subtle flex items-start gap-2 text-xs">
              <CalendarDays aria-hidden className="mt-px size-3.5 shrink-0" />
              <span>
                Nomor antrean dihitung berdasarkan urutan pendaftaran pada tanggal dan dokter yang
                sama.
              </span>
            </p>
          </CardBody>
        </Card>
      </form>
    </>
  );
}

/**
 * Page container for the reservation route.
 *
 * It now lives under the public shell rather than the patient sidebar, so it
 * supplies its own page padding instead of inheriting the portal's.
 */
export function ReservationLayout() {
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
      <ReservationScreen />
    </div>
  );
}

/** Shown when an anonymous visitor opens the reservation route. */
export function ReservationGate() {
  return (
    <div className="mx-auto max-w-md px-5 py-16">
      <Card>
        <EmptyState
          icon={<CalendarDays className="size-5" />}
          title="Masuk untuk membuat reservasi"
          description="Reservasi memerlukan akun Portal Pasien agar jadwal tercatat pada rekam medis Anda."
          action={
            <div className="flex gap-2">
              <LinkButton to="/patient/login" variant="primary">
                Masuk
              </LinkButton>
              <LinkButton to="/patient/register">Daftar</LinkButton>
            </div>
          }
        />
      </Card>
      {/* Plain sentence rather than an inline badge: a pill mid-paragraph
          breaks the line rhythm in a centred block. */}
      <p className="text-content-subtle mt-4 text-center text-xs">
        Tanpa akun, Anda tetap dapat menggunakan{" "}
        <Link to="/triage" className="text-accent font-semibold hover:underline">
          Triase Gejala
        </Link>{" "}
        dan chatbot.
      </p>
    </div>
  );
}
